import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { GripVertical, Plus, Save, X, Eye, EyeOff, ArrowLeft, Calendar, ImageOff, Edit, CheckSquare } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../../../../axiosConfig'; // Ensure this is correctly configured
// ============================================================================
// SINGLE ADD POPUP - Local Add Only + Thumbnail Preview
// ============================================================================
const DestinationsPopup = ({ selectedItem, onClose, onAddToCarousel, isMulti = false, selectedItems = [] }) => {
  
  const handleAddToCarousel = () => {
    const itemsToAdd = isMulti
      ? selectedItems.map(item => ({
          lastUpdated: new Date().toISOString().split('T')[0],
          selectedItem: item
        }))
      : [{
          lastUpdated: new Date().toISOString().split('T')[0],
          selectedItem
        }];
    onAddToCarousel(itemsToAdd);
    onClose();
  };
  const title = isMulti
    ? `Add ${selectedItems.length} Destinations`
    : `Add: ${selectedItem?.name || 'Item'}`;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-xl font-semibold text-slate-900">{title}</h2>
          <button onClick={onClose} className="text-slate-400 transition-colors hover:text-slate-500">
            <X size={24} />
          </button>
        </div>
        <div className="flex-1 px-6 py-6 space-y-6 overflow-y-auto">
          {isMulti ? (
            <div className="space-y-3">
              <p className="text-sm text-slate-500">Selected destinations:</p>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {selectedItems.map((item) => (
                  <div key={item.id} className="p-3 border border-slate-200 rounded bg-slate-50">
                    <p className="text-sm font-medium truncate">{item.name}</p>
                    <p className="text-xs text-slate-500">{item.city || 'Unknown'}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : selectedItem && (
            <div className="p-4 border border-slate-200 rounded-lg bg-slate-50">
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0">
                  <div className="relative w-32 h-32 overflow-hidden bg-slate-100 border-2 border-slate-300 border-dashed rounded-lg">
                    {selectedItem?.imageUrl ? (
                      <img src={selectedItem.imageUrl} alt={selectedItem.name} className="object-cover w-full h-full" />
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full text-slate-400">
                        <ImageOff size={32} />
                        <span className="mt-2 text-xs">No Image</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="mb-1 text-lg font-semibold text-slate-900">{selectedItem.name}</h3>
                  <p className="text-sm text-slate-500">{selectedItem.city || 'Unknown City'}</p>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-slate-50">
          <button onClick={onClose} className="px-6 py-2 font-medium text-slate-700 transition-colors border border-slate-300 rounded-lg hover:bg-slate-100">
            Cancel
          </button>
       
          <button
            onClick={handleAddToCarousel}
            className="flex items-center gap-2 px-6 py-2 font-medium text-white transition-colors bg-brand-blue rounded-lg hover:bg-brand-blue-dark"
          >
            <Plus className="w-4 h-4" />
            Add to Carousel
          </button>
        </div>
      </div>
    </div>
  );
};
// ============================================================================
// CONTEXT & STATE
// ============================================================================
const CMSContext = createContext();
const useCMS = () => {
  const context = useContext(CMSContext);
  if (!context) throw new Error('useCMS must be used within CMSProvider');
  return context;
};
// ============================================================================
// INITIAL SECTIONS
// ============================================================================
const initialSections = [
  {
    id: 'carousel-items',
    title: 'Selected Carousel Items',
    order: 1,
    visible: true,
    type: 'multiple',
    items: []
  }
];
// ============================================================================
// REUSABLE COMPONENTS
// ============================================================================
const Button = ({ children, onClick, variant = 'primary', size = 'md', className = '', icon: Icon, disabled, ...props }) => {
  const baseStyles = 'inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-all duration-200';
  const variants = {
    primary: 'bg-brand-blue hover:bg-brand-blue-dark text-white shadow-sm',
    secondary: 'bg-slate-100 hover:bg-slate-200 text-slate-700',
    danger: 'bg-red-600 hover:bg-red-700 text-white',
    ghost: 'hover:bg-slate-100 text-slate-500',
    success: 'bg-emerald-600 hover:bg-emerald-700 text-white'
  };
  const sizes = { sm: 'px-3 py-1.5 text-sm', md: 'px-4 py-2 text-sm', lg: 'px-6 py-3 text-base' };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      {...props}
    >
      {Icon && <Icon size={16} />}
      {children}
    </button>
  );
};
const Card = ({ children, className = '', onClick }) => {
  return (
    <div onClick={onClick} className={`bg-white rounded-xl shadow-sm border border-slate-200 ${className}`}>
      {children}
    </div>
  );
};
// ============================================================================
// BLOCK CARD
// ============================================================================
const ItemsBlockCard = ({ section, titleOverride, allowEdit = false }) => {
  const { items, title, visible } = section;
  const { removeFromBlock, updateSection, toggleSectionVisibility } = useCMS();
  const [editOpen, setEditOpen] = useState(false);
  const [editTitle, setEditTitle] = useState(title || '');
  const [editItemsPerBlock, setEditItemsPerBlock] = useState(section.itemsPerBlock || (items ? items.length : 0));
  
  const handleRemove = (itemId) => {
    removeFromBlock(section.id, itemId);
  };
  
  const openEdit = () => {
    setEditTitle(title || '');
    setEditItemsPerBlock(section.itemsPerBlock || items.length);
    setEditOpen(true);
  };
  
  const saveEdit = () => {
    updateSection({ ...section, title: editTitle, itemsPerBlock: Number(editItemsPerBlock) });
    setEditOpen(false);
  };
  
  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">{titleOverride || title}</h3>
          <p className="text-sm text-slate-500">{items.length} item(s)</p>
        </div>
        <div className="flex items-center gap-2">
          {allowEdit && (
            <button onClick={openEdit} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-sm text-slate-700">
              <Edit size={16} /> Edit
            </button>
          )}
          <button
            onClick={() => toggleSectionVisibility(section.id)}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-sm text-slate-700"
          >
            {visible ? <><Eye size={16} /> Visible</> : <><EyeOff size={16} /> Hidden</>}
          </button>
        </div>
      </div>
      {items.length === 0 ? (
        <div className="py-12 text-center text-slate-400">
          <p>No items added yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
          {items.map((item) => {
            const attraction = item.selectedItem;
            const imageUrl = attraction.imageUrl || null;
            return (
              <div key={item.id} className="relative overflow-hidden transition-all duration-200 bg-white border border-slate-200 rounded-lg shadow-sm group hover:shadow-md hover:-translate-y-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(item.id);
                  }}
                  className="absolute top-2 right-2 z-20 bg-white/90 hover:bg-red-50 text-slate-500 hover:text-red-600 rounded-full p-1.5 shadow-sm transition-all duration-200"
                  title="Remove from carousel"
                >
                  <X size={14} />
                </button>
                <div className="overflow-hidden bg-slate-100 aspect-square">
                  {imageUrl ? (
                    <img src={imageUrl} alt={attraction.name} className="object-cover w-full h-full" />
                  ) : (
                    <div className="flex items-center justify-center w-full h-full text-xs text-slate-400 bg-slate-200 border-2 border-dashed rounded-lg">
                      No Image
                    </div>
                  )}
                </div>
                <div className="p-3">
                  <h4 className="text-sm font-semibold text-center text-slate-900 truncate">{attraction.name}</h4>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {editOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="w-full max-w-md overflow-hidden bg-white rounded-lg shadow-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Edit Carousel Block</h2>
              <button onClick={() => setEditOpen(false)} className="text-slate-400 hover:text-slate-500"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block mb-2 text-sm font-medium text-slate-700">Block Title</label>
                <input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg" />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-slate-700">Items Per Block</label>
                <input type="number" min={1} value={editItemsPerBlock} onChange={(e) => setEditItemsPerBlock(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-slate-50">
              <button onClick={() => setEditOpen(false)} className="px-4 py-2 text-slate-700 border border-slate-300 rounded-lg">Cancel</button>
              <button onClick={saveEdit} className="px-4 py-2 text-white bg-brand-blue rounded-lg">Save</button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};
// ============================================================================
// CUSTOM MULTI-SELECT DROPDOWN COMPONENT
// ============================================================================
const MultiSelectDropdown = ({ label, options, selectedValues, onChange, placeholder = "— Select —" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const toggleOption = (value) => {
    const newValues = selectedValues.includes(value)
      ? selectedValues.filter(v => v !== value)
      : [...selectedValues, value];
    onChange(newValues);
  };
  const displayText = selectedValues.length === 0
    ? placeholder
    : `${selectedValues.length} selected`;
  return (
    <div className="relative" ref={wrapperRef}>
      <label className="block mb-2 text-sm font-medium text-slate-700">{label}</label>
      <div
        className="flex items-center justify-between w-full px-4 py-2 bg-white border border-slate-300 rounded-lg cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-blue"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={selectedValues.length === 0 ? "text-slate-400" : "text-slate-900"}>
          {displayText}
        </span>
        <span className="text-slate-400">▼</span>
      </div>
      {isOpen && (
        <div className="absolute z-10 w-full mt-1 overflow-y-auto bg-white border border-slate-300 rounded-lg shadow-lg max-h-60">
          {options.map((option) => (
            <label
              key={option.value}
              className="flex items-center gap-2 px-4 py-2 cursor-pointer hover:bg-slate-50"
            >
              <input
                type="checkbox"
                checked={selectedValues.includes(option.value)}
                onChange={() => toggleOption(option.value)}
                className="w-4 h-4 text-brand-blue border-slate-300 rounded focus:ring-brand-blue"
              />
              <span className="text-sm text-slate-700">{option.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};
// ============================================================================
// ATTRACTIONS GRID
// ============================================================================
const AttractionsGrid = ({ onAttractionClick, categoryIds, cityIds, tagIds, onAddSelected }) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  let urlCategoryIds = params.getAll('categoryId') || [];
  if (!urlCategoryIds || urlCategoryIds.length === 0) {
    urlCategoryIds = params.getAll('categoryIds[]') || [];
  }
  if ((!urlCategoryIds || urlCategoryIds.length === 0) && params.get('categoryId')) {
    const raw = params.get('categoryId');
    if (raw && raw.includes(',')) urlCategoryIds = raw.split(',').map(s => s.trim()).filter(Boolean);
  }
  const [attractions, setAttractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;
  
  const [selectedAttractionMap, setSelectedAttractionMap] = useState({}); // { [id]: attraction }
  const selectedAttractionIds = Object.keys(selectedAttractionMap).map(Number);
  
  const fetchAttractions = async () => {
    try {
      setLoading(true);
      setError(null);
      const start = (currentPage - 1) * itemsPerPage;
      const paramsObj = {
        start,
        length: itemsPerPage,
      };
      if (categoryIds?.length > 0) {
        paramsObj['categoryIds[]'] = categoryIds;
      } else if (urlCategoryIds?.length > 0) {
        paramsObj['categoryIds[]'] = urlCategoryIds;
      }
      if (cityIds?.length > 0) {
        paramsObj['cityIds[]'] = cityIds;
      }
      if (tagIds?.length > 0) {
        paramsObj['tagIds[]'] = tagIds;
      }
      const response = await axiosInstance.get(
        '/internal-api/v1/client-web/destination-panel/config/list',
        {
          params: paramsObj,
          paramsSerializer: params => {
            return Object.keys(params)
              .map(key => {
                const val = params[key];
                if (Array.isArray(val)) {
                  return val.map(v => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`).join('&');
                }
                return `${encodeURIComponent(key)}=${encodeURIComponent(val)}`;
              })
              .join('&');
          }
        }
      );
      if (response.data && response.data.data) {
        const rawItems = response.data.data.filter(item => item.isActive);
        const formatted = rawItems.map(item => ({
          id: item.id,
          uuid: item.uuid,
          name: item.name,
          city: item.cityName || 'Unknown',
          imageUrl: null,
          loadingImage: true
        }));
        setAttractions(formatted);
        setTotalItems(response.data.recordsTotal || 0);
        formatted.forEach(attr => fetchImageForAttraction(attr));
      }
    } catch (err) {
      console.error('Error fetching destinations:', err);
      setError('Failed to load attractions. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  const fetchImageForAttraction = async (attraction) => {
    if (!attraction.uuid) return;
    try {
      const response = await axiosInstance.get(
        `/internal-api/v1/client-web/destination-panel/config/${attraction.uuid}/view`
      );
      if (response.data && response.data.result && response.data.result.images && response.data.result.images.length > 0) {
        const sortedImages = response.data.result.images.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
        const firstImage = sortedImages[0].image;
        const baseUrl = response.data.result.localImageServer || '';
        const imagePath = firstImage.path || '';
        const fullUrl = baseUrl + imagePath;
        setAttractions(prev => prev.map(a =>
          a.id === attraction.id ? { ...a, imageUrl: fullUrl, loadingImage: false } : a
        ));
      } else {
        setAttractions(prev => prev.map(a =>
          a.id === attraction.id ? { ...a, loadingImage: false } : a
        ));
      }
    } catch (err) {
      console.error(`Error fetching image for ${attraction.uuid}:`, err);
      setAttractions(prev => prev.map(a =>
        a.id === attraction.id ? { ...a, loadingImage: false } : a
      ));
    }
  };
  useEffect(() => {
    fetchAttractions();
  }, [currentPage, location.search, categoryIds, cityIds, tagIds]);
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  
  const toggleSelection = (attraction) => {
    setSelectedAttractionMap((prev) => {
      const newMap = { ...prev };
      if (newMap[attraction.id]) {
        delete newMap[attraction.id];
      } else {
        newMap[attraction.id] = attraction;
      }
      return newMap;
    });
  };
  
  const selectedAttractions = Object.values(selectedAttractionMap);
  
  const handleBulkAdd = () => {
    if (selectedAttractions.length === 0) return;
    onAttractionClick(null, selectedAttractions);
  };
  return (
    <div className="p-6 bg-white border border-slate-200 shadow-sm rounded-xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">All Destinations</h3>
          <p className="text-sm text-slate-500">Loaded from database</p>
        </div>
        <div className="flex items-center gap-4">
          <span className="bg-slate-100 px-3 py-1.5 rounded-lg text-sm font-medium text-slate-500">
            {attractions.length} of {totalItems} Attractions
          </span>
          {selectedAttractionIds.length > 0 && (
            <button
              onClick={() => onAddSelected(selectedAttractions)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white transition-colors bg-emerald-600 rounded-lg hover:bg-emerald-700"
            >
              <CheckSquare size={16} />
              Add Selected {selectedAttractionIds.length} to Carousel
            </button>
          )}
        </div>
      </div>
      {error && <div className="mb-4 text-sm text-center text-red-600">{error}</div>}
      {loading ? (
        <div className="py-12 text-center text-slate-500">Loading attractions...</div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 mb-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
            {attractions.map((attraction) => {
              const isSelected = selectedAttractionIds.includes(attraction.id);
              return (
                <div
                  key={attraction.id}
                  className={`group relative overflow-hidden rounded-lg border ${
                    isSelected ? 'border-emerald-500 ring-2 ring-emerald-300' : 'border-slate-200'
                  } bg-white shadow-sm transition-all duration-200 hover:shadow-md hover:-translate-y-1`}
                >
                  <div className="absolute z-20 top-2 right-12">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelection(attraction)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-5 h-5 text-emerald-600 bg-white border-slate-300 rounded focus:ring-emerald-500"
                    />
                  </div>
                  <div
                    onClick={() => onAttractionClick(attraction)}
                    className="cursor-pointer"
                  >
                    <div className="overflow-hidden bg-slate-100 aspect-square">
                      {attraction.loadingImage ? (
                        <div className="w-full h-full bg-slate-200 animate-pulse" />
                      ) : attraction.imageUrl ? (
                        <img src={attraction.imageUrl} alt={attraction.name} className="object-cover w-full h-full" />
                      ) : (
                        <div className="flex items-center justify-center w-full h-full text-xs text-slate-400 bg-slate-200 border-2 border-dashed rounded-lg">
                          No Image
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      <h4 className="text-sm font-semibold text-center text-slate-900 truncate">{attraction.name}</h4>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button size="sm" variant="secondary" onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1}>
                Previous
              </Button>
              <span className="text-sm text-slate-500">Page {currentPage} of {totalPages}</span>
              <Button size="sm" variant="secondary" onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages}>
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
// ============================================================================
// CAROUSEL SETTINGS FORM
// ============================================================================
const CarouselSettingsForm = ({ onSet }) => {
  const [blockName, setBlockName] = useState('');
  const [subHeader, setSubHeader] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [itemsPerBlock, setItemsPerBlock] = useState('8');
  const [displayOrder, setDisplayOrder] = useState('1');
  const [validFrom, setValidFrom] = useState('');
  const [validTill, setValidTill] = useState('');
  
  const [categories, setCategories] = useState([]);
  const [subCategoryToCategoryMap, setSubCategoryToCategoryMap] = useState({});
  const [cities, setCities] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const res = await axiosInstance.get('/internal-api/v1/client-web/cms/page/page-blocks/meta');
        
        if (res.data?.status !== true || !res.data?.result) {
          throw new Error('Invalid meta response');
        }
        
        const result = res.data.result;
        
        if (Array.isArray(result.subCategories)) {
          const mapping = {};
          result.subCategories.forEach((item) => {
            if (item?.id != null && item?.categoryId != null) {
              mapping[String(item.id)] = String(item.categoryId);
            }
          });
          setSubCategoryToCategoryMap(mapping);
          setCategories(result.subCategories.map(item => ({
            value: String(item.id),
            label: item.name || item.code || `Category ${item.id}`
          })));
        }
        
        if (Array.isArray(result.cities)) {
          setCities(result.cities.map(item => ({
            value: String(item.id),
            label: item.name || item.code || `City ${item.id}`
          })));
        }
        
        if (Array.isArray(result.destinationTags)) {
          setTags(
            result.destinationTags
              .filter(item => item.code && item.name && item.name.trim() !== '')
              .map(item => ({
                value: String(item.code),
                label: item.name.trim()
              }))
          );
        }
        
      } catch (err) {
        console.error('Failed to load CMS meta:', err);
        setError('Could not load dropdown options');
      } finally {
        setLoading(false);
      }
    };
    
    fetchMeta();
  }, []);
  
  const isBlockNameValid = blockName.trim().length > 0;
  
  const handleSet = () => {
    if (!isBlockNameValid) {
      alert('Please enter a Page Name before setting.');
      return;
    }
    
    if (!validFrom || !validTill) {
      alert('Please select carousel valid period (Valid From & Valid Till).');
      return;
    }
    
    if (validTill < validFrom) {
      alert('Valid Till cannot be earlier than Valid From.');
      return;
    }
    
    onSet({
      blockName,
      subHeader,
      selectedCategories,
      selectedCategoryIds: Array.from(new Set(
        selectedCategories
          .map(id => subCategoryToCategoryMap[String(id)])
          .filter(Boolean)
      )),
      selectedCities,
      selectedTags,
      itemsPerBlock: Number(itemsPerBlock),
      displayOrder: Number(displayOrder),
      validFrom,
      validTill
    });
  };
  
  if (loading) {
    return (
      <Card className="p-6 mb-8">
        <h3 className="mb-4 text-xl font-semibold text-slate-900">Carousel Block Settings</h3>
        <div className="py-8 text-center text-slate-500">Loading options from server...</div>
      </Card>
    );
  }
  
  if (error) {
    return (
      <Card className="p-6 mb-8 border-red-200">
        <h3 className="mb-4 text-xl font-semibold text-slate-900">Carousel Block Settings</h3>
        <div className="py-4 text-center text-red-600">{error}</div>
      </Card>
    );
  }
  
  return (
    <Card className="p-6 mb-8">
      <h3 className="mb-6 text-xl font-semibold text-slate-900">Carousel Block Settings</h3>
      
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        <div>
          <label className="block mb-2 text-sm font-medium text-slate-700">
            Page Name <span className="text-red-600">*</span>
          </label>
          <input
            type="text"
            value={blockName}
            onChange={(e) => setBlockName(e.target.value)}
            placeholder="e.g. Featured Summer Destinations"
            className={`w-full px-4 py-2 border ${
              blockName.trim() ? 'border-slate-300' : 'border-red-300 focus:border-red-500'
            } rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue`}
          />
          {!blockName.trim() && (
            <p className="mt-1 text-xs text-red-600">Page Name is required</p>
          )}
        </div>
        
        <div>
          <label className="block mb-2 text-sm font-medium text-slate-700">Sub Header</label>
          <input
            type="text"
            value={subHeader}
            onChange={(e) => setSubHeader(e.target.value)}
            placeholder="e.g. Discover the best places to visit this season"
            className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
          />
        </div>
        
        <MultiSelectDropdown
          label="Category"
          options={categories}
          selectedValues={selectedCategories}
          onChange={setSelectedCategories}
          placeholder="— All Categories —"
        />
        
        <MultiSelectDropdown
          label="City"
          options={cities}
          selectedValues={selectedCities}
          onChange={setSelectedCities}
          placeholder="— All Cities —"
        />
        
        <MultiSelectDropdown
          label="Tag"
          options={tags}
          selectedValues={selectedTags}
          onChange={setSelectedTags}
          placeholder="— All Tags —"
        />
        
        <div>
          <label className="block mb-2 text-sm font-medium text-slate-700">Items per Block</label>
          <input
            type="number"
            min="1"
            value={itemsPerBlock}
            onChange={(e) => setItemsPerBlock(e.target.value)}
            className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
          />
        </div>
        
        <div>
          <label className="block mb-2 text-sm font-medium text-slate-700">
            Valid From <span className="text-red-600">*</span>
          </label>
          <input
            type="date"
            value={validFrom}
            onChange={(e) => setValidFrom(e.target.value)}
            className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
          />
        </div>
        
        <div>
          <label className="block mb-2 text-sm font-medium text-slate-700">
            Valid Till <span className="text-red-600">*</span>
          </label>
          <input
            type="date"
            value={validTill}
            onChange={(e) => setValidTill(e.target.value)}
            className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
          />
        </div>
        
        <div>
          <label className="block mb-2 text-sm font-medium text-slate-700">Display Order</label>
          <input
            type="number"
            min="1"
            value={displayOrder}
            onChange={(e) => setDisplayOrder(e.target.value)}
            className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-blue"
          />
        </div>
      </div>
      
      <div className="flex justify-end mt-6">
        <button
          onClick={handleSet}
          disabled={!isBlockNameValid}
          className={`px-6 py-2 rounded-lg font-medium transition-colors ${
            isBlockNameValid
              ? 'bg-brand-blue text-white hover:bg-brand-blue-dark'
              : 'bg-slate-300 text-slate-500 cursor-not-allowed'
          }`}
        >
          Set
        </button>
      </div>
    </Card>
  );
};
// ============================================================================
// CMS LAYOUT
// ============================================================================
const CMSLayout = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  let categoryIds = params.getAll('categoryId') || [];
  if (!categoryIds || categoryIds.length === 0) {
    categoryIds = params.getAll('categoryIds[]') || [];
  }
  if ((!categoryIds || categoryIds.length === 0) && params.get('categoryId')) {
    const raw = params.get('categoryId');
    if (raw && raw.includes(',')) categoryIds = raw.split(',').map(s => s.trim()).filter(Boolean);
  }
  const blockTitle = params.get('blockTitle') || 'Default Carousel';
  const isEditFromBlockList = Boolean(params.get('blockId'));
  
  const { sections, toggleSectionVisibility, addToCarousel, updateSection, removeFromBlock } = useCMS();

  const [popupOpen, setPopupOpen] = useState(false);
  const [selectedAttraction, setSelectedAttraction] = useState(null);
  const [selectedAttractionsMulti, setSelectedAttractionsMulti] = useState([]);
  const [publishing, setPublishing] = useState(false);
  const navigate = useNavigate();
  
  const [isSet, setIsSet] = useState(false);
  const [settings, setSettings] = useState({});

  const carouselSection = sections.find(s => s.id === 'carousel-items') || {};
  const carouselItems = carouselSection.items || [];
  
  const formatDateTime = (dateStr) => dateStr ? `${dateStr} 00:00:00` : '';
  
  const pageMeta = params.get('pageMeta') || '';

  const handleSet = (newSettings) => {
    setSettings(newSettings);
    setIsSet(true);
  };
  
  const logFormData = (fd, label = "FormData") => {
    console.log(`\n==================== ${label} ====================`);
    for (const [key, value] of fd.entries()) {
      console.log(`${key}:`, value);
    }
    console.log("===================================================\n");
  };
  
  const handlePublish = async () => {
    if (carouselItems.length === 0) {
      alert('No items in the carousel to publish.');
      return;
    }

    if (!settings.validFrom || !settings.validTill) {
      alert('Please set carousel Valid From and Valid Till in Carousel Settings.');
      return;
    }

    if (!window.confirm(`Publish all ${carouselItems.length} attractions as ONE carousel group?`)) {
      return;
    }

    setPublishing(true);

    const effectiveTitle = settings.blockName || carouselSection.title || pageMeta || blockTitle || 'Selected Carousel Items';
    const effectiveSubHeader = settings.subHeader || '';
    const effectiveMaxItems = settings.itemsPerBlock || carouselSection.itemsPerBlock || carouselItems.length || 0;
    const effectiveDisplayOrder = settings.displayOrder || carouselSection.displayOrder || 1;

    const formdata = new FormData();
    formdata.append('pageCode', 'CUSTOM_DESTINATION_VIEW');
    formdata.append('name', effectiveTitle);
    formdata.append('displayOrder', effectiveDisplayOrder.toString());
    formdata.append('blockHeader', effectiveTitle);
    formdata.append('subHeader', effectiveSubHeader);
    formdata.append('pageMeta', settings.blockName || '');
    formdata.append('isVisible', carouselSection.visible ? '1' : '0');

    if (settings.selectedCategories?.length > 0) {
      settings.selectedCategories.forEach(subCategoryId => {
        formdata.append('destinationCarousels[0][categoryIds][]', subCategoryId);
      });
    }
    if (settings.selectedCities?.length > 0) {
      settings.selectedCities.forEach(city => {
        formdata.append('destinationCarousels[0][cityIds][]', city);
      });
    }
    if (settings.selectedTags?.length > 0) {
      settings.selectedTags.forEach(tag => {
        formdata.append('destinationCarousels[0][tagCodes][]', tag);
      });
    }

    formdata.append('blockContent', '');
    formdata.append('link', 'htt');

    formdata.append('validFrom', formatDateTime(settings.validFrom));
    formdata.append('validTill', formatDateTime(settings.validTill));
    formdata.append('isPublic', '1');
    formdata.append('destinationCarousels[0][maxItems]', effectiveMaxItems.toString());
    formdata.append('destinationCarousels[0][cardAspect]', '1:1');
    formdata.append('destinationCarousels[0][isActionable]', '0');

    carouselItems.forEach((item, index) => {
      const attraction = item.selectedItem;
      if (attraction?.uuid) {
        formdata.append(`destinationCarousels[0][carouselCards][${index}][destinationCard][destinationUuid]`, attraction.uuid);
        formdata.append(`destinationCarousels[0][carouselCards][${index}][destinationCard][displayOrder]`, (index + 1).toString());
      }
    });

    try {
      logFormData(formdata, "Publish Destination Carousel Payload");
      
      const response = await axiosInstance.post(
        '/internal-api/v1/client-web/cms/page/page-blocks/destination-carousel/new-group',
        formdata
      );

      if (response && response.status >= 200 && response.status < 300) {
        alert('Carousel group published successfully as ONE group!');
        navigate('/LandingPage');
      } else if (response.data && response.data.result) {
        alert('Carousel group published successfully as ONE group!');
        navigate('/LandingPage');
      } else {
        alert('Published, but please verify the result.');
      }
    } catch (error) {
      console.error('Error publishing carousel group:', error);
      alert('Failed to publish carousel group. Check console for details.');
    } finally {
      setPublishing(false);
    }
  };
  
  const handleAttractionClick = (singleItem, multiItems = null) => {
    if (multiItems) {
      setSelectedAttractionsMulti(multiItems);
      setSelectedAttraction(null);
    } else {
      setSelectedAttraction(singleItem);
      setSelectedAttractionsMulti([]);
    }
    setPopupOpen(true);
  };
  
  const handleBulkSelectionConfirm = (items) => {
    if (items.length === 0) return;
    handleAttractionClick(null, items);
  };
  
  return (
    <div className="space-y-4">
      {/* Page Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Add Custom Page</h1>
          <div className="mt-1 text-sm text-slate-500">
            Home <span className="mx-1.5 text-slate-400">/</span>
            Cms <span className="mx-1.5 text-slate-400">/</span>
            <span className="font-semibold text-slate-700">Add Custom Page</span>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" icon={Eye}>Preview Page</Button>
          <Button
            onClick={handlePublish}
            icon={Save}
            disabled={publishing || carouselItems.length === 0}
            variant="success"
          >
            {publishing ? 'Publishing...' : `Publish (${carouselItems.length})`}
          </Button>
        </div>
      </div>

      <div className="p-4 border rounded-lg border-brand-blue/20 bg-brand-blue/5">
        <p className="text-sm text-brand-blue">
          All attractions added to <strong>Carousel</strong> will be published as <strong>ONE single custom destination page</strong>.
        </p>
      </div>

      <CarouselSettingsForm onSet={handleSet} />
      {isSet && (
        <>
          <ItemsBlockCard
            section={carouselSection}
            titleOverride={settings.blockName || 'Selected Carousel Items'}
            allowEdit={isEditFromBlockList}
          />
          <AttractionsGrid
            onAttractionClick={handleAttractionClick}
            categoryIds={settings.selectedCategories}
            cityIds={settings.selectedCities}
            tagIds={settings.selectedTags}
            onAddSelected={handleBulkSelectionConfirm}
          />
        </>
      )}

      <Button onClick={() => navigate('/CMSDashboard')} variant="secondary" icon={ArrowLeft} className="w-full">
        Back to CMS Dashboard
      </Button>

      {popupOpen && (
        <DestinationsPopup
          selectedItem={selectedAttraction}
          selectedItems={selectedAttractionsMulti}
          isMulti={selectedAttractionsMulti.length > 0}
          onClose={() => {
            setPopupOpen(false);
            setSelectedAttraction(null);
            setSelectedAttractionsMulti([]);
          }}
          onAddToCarousel={addToCarousel}
        />
      )}
    </div>
  );
};
// ============================================================================
// MAIN COMPONENT
// ============================================================================
const ViewMore = () => {
  const [sections, setSections] = useState(initialSections);
  const location = useLocation();
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const blockTitleParam = params.get('blockTitle');
      const pageMetaParam = params.get('pageMeta');
      if (blockTitleParam || pageMetaParam) {
        setSections(prev => prev.map(s => {
          if (s.id !== 'carousel-items') return s;
          const updated = { ...s };
          if (pageMetaParam) updated.title = pageMetaParam;
          if (blockTitleParam) updated.title = blockTitleParam;
          return updated;
        }));
      }
    } catch (err) {
      console.error('Failed to apply URL params to carousel section', err);
    }
  }, [location.search]);
  const updateSection = (updatedSection) => {
    setSections(sections.map(s => s.id === updatedSection.id ? updatedSection : s));
  };
  const toggleSectionVisibility = (sectionId) => {
    setSections(sections.map(s => s.id === sectionId ? { ...s, visible: !s.visible } : s));
  };
  const addToCarousel = (newItems) => {
    const items = Array.isArray(newItems) ? newItems : [newItems];
    setSections(sections.map(section =>
      section.id === 'carousel-items'
        ? { ...section, items: [...section.items, ...items.map(i => ({ ...i, id: Date.now() + Math.random() }))] }
        : section
    ));
  };
  const addToPageBody = (newItems) => {
    const items = Array.isArray(newItems) ? newItems : [newItems];
    setSections(sections.map(section =>
      section.id === 'page-body-items'
        ? { ...section, items: [...section.items, ...items.map(i => ({ ...i, id: Date.now() + Math.random() }))] }
        : section
    ));
  };
  const removeFromBlock = (sectionId, itemId) => {
    setSections(sections.map(section =>
      section.id === sectionId
        ? { ...section, items: section.items.filter(item => item.id !== itemId) }
        : section
    ));
  };
  const contextValue = {
    sections,
    updateSection,
    toggleSectionVisibility,
    addToCarousel,
    addToPageBody,
    removeFromBlock
  };
  return (
    <CMSContext.Provider value={contextValue}>
      <CMSLayout />
    </CMSContext.Provider>
  );
};
export default ViewMore;